import { ExerciseStatus } from "../../constants/exercises";
import { getMinutesAndSeconds } from "../common/exerciseHelper";
import { Card, NtfExercises, NtfState, NtfUserScore } from "./ntfSlice";
import { shuffle } from 'lodash'

export interface NtfCurrent {
  currentExercise: number
}

export function isReadyToStart(status: ExerciseStatus, exercises?: NtfExercises): boolean {
  return status === 'start' && exercises !== undefined
}

export const getCurrent = (userScores: NtfUserScore[]): NtfCurrent => {
  let currentExercise: number = userScores.length
  if (currentExercise === 0) {
    currentExercise = 1
  } else {
    const { end } = userScores[userScores.length - 1]
    if (end !== undefined) {
      currentExercise += 1
    }
  }
  return { currentExercise }
}


export type Gender = 'M' | 'F'


export type GenderLabels = {
  [key in Gender]: string
}

export interface SelectedLevelAndGender {
  level: number
  gender?: Gender
}

export const genderLabels: GenderLabels = {
  M: 'Male',
  F: 'Female',
}
  

export const getLastSelectedLevelAndGender = (userScores: NtfUserScore[]): SelectedLevelAndGender | null  => {
  if (userScores.length) {
    const lastScore = userScores[userScores.length - 1]
    const { level, gender } = lastScore
    return { level, gender }
  }
  return null
}

export const startExercise = (level: number, gender: Gender | undefined, state: NtfState) => {
  const { userScores } = state
  const scoreLength = userScores.length
  const start = Date.now()
  if (scoreLength === 0) {
    userScores.push({ level, gender, start })
    return
  }
  const scoreIndex = scoreLength - 1
  const userScore = userScores[scoreIndex]
  if (userScore.end !== undefined) {
    userScores.push({ level, gender, start })
  }
}

export const endExercise = (finished: boolean, attempts: number, state: NtfState) => {
  state.cards = undefined
  const { userScores } = state
  const scoreLength = userScores.length
  if (scoreLength > 0) {
    const lastScore = userScores[scoreLength - 1]
    if (lastScore.end === undefined) {
      lastScore.end = Date.now()
      lastScore.attempts = attempts
      lastScore.finished = finished
    }
  }
}

export const moveNext = (state: NtfState, repeat?: boolean) => {
  const { status, exercises, userScores } = state
  if (status === 'start') {
    if (exercises !== undefined) {
      state.startTime = Date.now()
      state.status = 'in-progress'
    }
    return
  }
  if (status === 'completed') {
    if (!repeat) {
      quitExercise(state)
    } else {
      state.status = 'in-progress'
    }
    return  
  }
  if (status === 'check-point') {
    state.status = 'in-progress'
  }
  if (status === 'in-progress') {
    if (exercises) {
      const lastScore = userScores[userScores.length - 1]
      if (userScores.length >= exercises.count) {
        if (lastScore.end !== undefined) {
          state.endTime = Date.now()
          state.status = 'completed'
          return
        }
      } else {
        if (lastScore.end !== undefined) {
          state.endTime = Date.now()
          state.status = 'check-point'
          return
        }
      }
    }
    state.endTime = Date.now()
  }
}

export const quitExercise = (state: NtfState) => {
  state.status = 'start'
  state.id = undefined
  state.exercises = undefined
  state.cards = undefined
  state.userScores = []
  state.startTime = undefined
  state.endTime = undefined
}

export interface NtfResult {
  level: number
  gender?: Gender
  time: string
  attempts?: number
}

export const getResults = (status: ExerciseStatus, userScores: NtfUserScore[]): NtfResult[] => {
  let results: NtfResult[] = []
  if (status === 'completed' || status === 'check-point') {
    results = userScores.map(userScore => {
      const { mins, seconds } = getMinutesAndSeconds(userScore.start, userScore.end)
      return {
        level: userScore.level,
        gender: userScore.gender,
        time: `${mins}:${seconds.toString().padStart(2, '0')}`,
        attempts: userScore.attempts
      }
    })
    results.reverse()
  }
  return results;
}

export const getCardKeys = (cards: Card[], level: number | undefined): string[] => {
  const faceKeys = cards.map((_, index) => `${index}F`)
  const nameKeys = cards.map((_, index) => `${index}N`)
  switch(level) {
    case 1:
      return faceKeys.concat(shuffle(nameKeys))
    case 2:
      return shuffle(faceKeys).concat(shuffle(nameKeys))
    case 3:
    case 4:
    default:
      return shuffle(faceKeys.concat(nameKeys))  
  }
}
  

